import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { BellIcon, MessageSquareIcon, MailIcon, PhoneIcon, MapPinIcon, UsersIcon, ChevronRightIcon } from 'lucide-react';
import { useWebinar } from '../../hooks/useWebinar';
const CHANNEL_ICONS = [BellIcon, MessageSquareIcon, MailIcon, PhoneIcon];
const EASE = [0.23, 1, 0.32, 1] as const;

/** Product-inspired Communications ATNA interface. All figures are illustrative. */
export function CommandCenter() {
  const reduce = useReducedMotion();
  const c = useWebinar().copy.commandCenter;
  const [step, setStep] = useState(reduce ? 3 : 0);
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setStep(s => (s + 1) % 4), 2600);
    return () => window.clearInterval(id);
  }, [reduce]);
  const active = (i: number) => step >= i;
  return <div className="relative w-full max-w-[560px] mx-auto" data-id="element-7789" data-mp="3437bx73bibf2">
      <div className="rounded-[22px] border border-white/10 bg-[#0E1330] shadow-[0_40px_90px_-40px_rgba(79,70,229,0.55)] overflow-hidden" data-id="element-7790" data-mp="3918rvj5g5umb">
        {/* Window bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.07]" data-id="element-7791" data-mp="3c8vpw2u3dyl">
          <div className="flex items-center gap-2" data-id="element-7792" data-mp="3ui0iclhek8gn">
            <span className="w-2 h-2 rounded-full bg-[#A5A1FF]" aria-hidden="true" data-id="element-7793" data-mp="3uite6retpdk0" />
            <span className="font-sans text-[12px] text-[#A6ABC8]" data-id="element-7794" data-mp="3qhzru2h146t8">{c.module}</span>
          </div>
          <span className="rounded-full border border-white/10 px-2.5 py-0.5 font-sans text-[11px] text-[#A6ABC8]" data-id="element-7795" data-mp="r09z6ql5o475">{c.status}</span>
        </div>

        <div className="p-5 md:p-6" data-id="element-7796" data-mp="i0v8pr4zntmn">
          <h3 className="font-serif text-[22px] text-white" data-id="element-7797" data-mp="i03lp4pgj47z">{c.title}</h3>

          {/* Stepper */}
          <ol className="mt-4 flex items-center gap-1.5 flex-wrap" aria-label={c.stepsAria} data-id="element-7798" data-mp="d0qqnwofmqub">
            {c.steps.map((s, i) => <li key={s} className="flex items-center gap-1.5" data-id="element-7799" data-mp="32sfatp4frjml" data-mpi={i}>
                <span className={`rounded-full px-2.5 py-1 font-sans text-[10.5px] font-semibold tracking-[0.12em] uppercase transition-colors duration-200 ${step === i ? 'bg-[#4F46E5] text-white' : active(i) ? 'bg-white/10 text-white' : 'bg-white/[0.04] text-[#6F7598]'}`} data-id="element-7800" data-mp="2xqzm9n31uuzg">
                  {s}
                </span>
                {i < c.steps.length - 1 && <ChevronRightIcon className="w-3.5 h-3.5 text-[#4C5274]" aria-hidden="true" data-id="element-7801" data-mp="2u8iogtqw1d9u" />}
              </li>)}
          </ol>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7802" data-mp="2l9f6jt3gj3wx">
            {/* Audience */}
            <Panel on={active(0)} label={c.steps[0]} data-id="element-7803" data-mp="26kspnmy6m43l">
              <ul className="space-y-1.5" data-id="element-7804" data-mp="26m3ttnvjhdv8">
                {c.audience.map((a, i) => <li key={a} className="flex items-center gap-2 font-sans text-[13px] text-white/90" data-id="element-7805" data-mp="2bkdq61t02kif" data-mpi={i}>
                    {i === 0 ? <MapPinIcon className="w-3.5 h-3.5 text-[#A5A1FF]" aria-hidden="true" data-id="element-7806" data-mp="1xkmew8tvbi84" /> : <UsersIcon className="w-3.5 h-3.5 text-[#A5A1FF]" aria-hidden="true" data-id="element-7807" data-mp="22hw4ppl65vwh" />}
                    {a}
                  </li>)}
              </ul>
              <p className="mt-3 font-serif text-[20px] text-white" data-id="element-7808" data-mp="1dykjyxuoipnj">{c.audienceCount}</p>
            </Panel>

            {/* Message */}
            <Panel on={active(1)} label={c.steps[1]} data-id="element-7809" data-mp="1rzdh7qcd7yuc">
              <p className="font-sans text-[12.5px] leading-relaxed text-white/85" data-id="element-7810" data-mp="1nwgsdhw8f6hy">{c.message}</p>
              <div className="mt-3 flex gap-1.5" data-id="element-7811" data-mp="21vqain400aok">
                <span className="rounded bg-white/10 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-white" data-id="element-7812" data-mp="21uf38dbcgksp">FR</span>
                <span className="rounded bg-white/[0.05] px-1.5 py-0.5 font-sans text-[10px] font-semibold text-[#A6ABC8]" data-id="element-7813" data-mp="1wv2ji1frrtuc">EN</span>
              </div>
            </Panel>
          </div>

          {/* Channels */}
          <Panel on={active(2)} label={c.steps[2]} className="mt-3" data-id="element-7814" data-mp="2kmhgrfsovkyq">
            <ul className="grid grid-cols-4 gap-2" data-id="element-7815" data-mp="2kmr9hb54c684">
              {c.channels.map((ch, i) => {
              const Icon = CHANNEL_ICONS[i];
              return <li key={ch} data-id="element-7816" data-mp="dxfqxgngg3vl" data-mpi={i}>
                    <motion.div animate={{
                  backgroundColor: active(2) ? 'rgba(79,70,229,0.22)' : 'rgba(255,255,255,0.03)'
                }} transition={{
                  duration: 0.2,
                  delay: reduce ? 0 : i * 0.05,
                  ease: EASE
                }} className="flex flex-col items-center gap-1.5 rounded-xl border border-white/[0.08] py-2.5" data-id="element-7817" data-mp="dw4lzdi9wq2w">
                      <Icon className={`w-4 h-4 ${active(2) ? 'text-[#C7C4FF]' : 'text-[#6F7598]'}`} aria-hidden="true" data-id="element-7818" data-mp="3r6bt7hgbnkia" />
                      <span className="font-sans text-[11px] text-white/85" data-id="element-7819" data-mp="k5sptxilz6">{ch}</span>
                    </motion.div>
                  </li>;
            })}
            </ul>
          </Panel>

          {/* Results */}
          <Panel on={active(3)} label={c.steps[3]} className="mt-3" data-id="element-7820" data-mp="1ffpy8amm0i55">
            <dl className="grid grid-cols-3 divide-x divide-white/[0.08]" data-id="element-7821" data-mp="1fhkjjq1smc9r">
              {c.results.map((r, i) => <div key={r.label} className={i === 0 ? 'pr-3' : 'px-3'} data-id="element-7822" data-mp="1uhygiryd13re" data-mpi={i}>
                  <dt className="font-sans text-[10.5px] tracking-[0.1em] uppercase text-[#8A90B2]" data-id="element-7823" data-mp="1ukcbz1wychzh">{r.label}</dt>
                  <dd className={`mt-1 font-serif text-[22px] ${i === 2 ? 'text-[#C7C4FF]' : 'text-white'}`} data-id="element-7824" data-mp="1ksewhiywv3uy">{r.value}</dd>
                </div>)}
            </dl>
            <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden="true" data-id="element-7825" data-mp="1goqnqwyjm284">
              <motion.div className="h-full rounded-full bg-[#4F46E5]" animate={{
              width: active(3) ? '92%' : '0%'
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} data-id="element-7826" data-mp="1bpnhj96ttpyk" />
            </div>
          </Panel>
        </div>
      </div>
      <p className="mt-3 text-right font-sans text-[11px] text-[#6F7598]" data-id="element-7827" data-mp="xts4hjz3z933">{c.illustrative}</p>
    </div>;
}
function Panel({
  on,
  label,
  children,
  className = ''
}: {
  on: boolean;
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl border p-4 transition-colors duration-200 ${on ? 'border-[#4F46E5]/50 bg-white/[0.04]' : 'border-white/[0.07] bg-white/[0.02]'} ${className}`} data-id="element-7828" data-mp="pit8xx4kgc6v">
      <p className="mb-2.5 font-sans text-[10.5px] font-semibold tracking-[0.14em] uppercase text-[#8A90B2]" data-id="element-7829" data-mp="gimsq3hhgy40">{label}</p>
      {children}
    </div>;
}